// Template form state (#288) — the fields half of <TemplateFormModal/>, split
// out for the `createMachineRemove` reason: field state, the JSON parse and the
// "may this submit" rule are state, not rendering.
//
// A WRITE IS A FULL REPLACE. `upsertTemplateByName` takes `excluded.*` for every
// mutable column, so a field this form omits does not keep its old value — it
// resets to the column default. That is why `invocation` is edited here as raw
// JSON rather than left out: a template authored over MCP with an invocation
// override would otherwise have it silently wiped by a UI edit that never
// mentioned it. Round-tripping it is the difference between an edit and a
// partial overwrite.
//
// The refusal rules the API owns are NOT re-implemented: the compatible-backend/
// base_url pairing (422) and every field bound are the server's answer and render
// inline from its `detail`. What this holds is the one thing the server cannot
// answer for us — whether the text in the invocation box is JSON at all, which
// has to be decided before there is a request to send.

import { type AgentBackend, agentBackendNeedsBaseUrl } from '@developerz/domain';
import { batch, createSignal } from 'solid-js';
import type { ApiTemplate, TemplateWrite } from './agent-templates';

export type TemplateFormController = {
  readonly name: () => string;
  readonly setName: (v: string) => void;
  readonly program: () => string;
  readonly setProgram: (v: string) => void;
  readonly backend: () => string;
  readonly setBackend: (v: string) => void;
  readonly baseUrl: () => string;
  readonly setBaseUrl: (v: string) => void;
  readonly model: () => string;
  readonly setModel: (v: string) => void;
  readonly kbRef: () => string;
  readonly setKbRef: (v: string) => void;
  readonly systemPrompt: () => string;
  readonly setSystemPrompt: (v: string) => void;
  readonly invocation: () => string;
  readonly setInvocation: (v: string) => void;
  /** True when this backend has no default endpoint — a `base_url` hint, not a gate. */
  readonly needsBaseUrl: () => boolean;
  /** Non-null when the invocation box holds something that is not JSON. */
  readonly invocationError: () => string | null;
  /** May this form submit? Name present, invocation parseable. */
  readonly valid: () => boolean;
  /** The wire body — every field, because the API replaces every field. */
  readonly body: () => TemplateWrite;
  /**
   * (Re)seed every field from a template, or from the API's own defaults for a
   * create. Called when the modal opens on a different row — the modal stays
   * mounted so its exit animation can play, so seeding is what makes "open on
   * template B" show B rather than whatever A was left holding.
   */
  readonly seed: (initial: ApiTemplate | null) => void;
};

/** Trimmed text, or `null` — an empty box CLEARS the column, it does not keep it. */
const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** `invalidJson` is the copy shown when the invocation box does not parse. */
export function createTemplateForm(invalidJson: string): TemplateFormController {
  const [name, setName] = createSignal('');
  const [program, setProgram] = createSignal('custom');
  const [backend, setBackend] = createSignal('none');
  const [baseUrl, setBaseUrl] = createSignal('');
  const [model, setModel] = createSignal('');
  const [kbRef, setKbRef] = createSignal('');
  const [systemPrompt, setSystemPrompt] = createSignal('');
  const [invocation, setInvocation] = createSignal('');

  /** The parsed invocation: the value to send, or the reason it can't be sent. */
  const parsedInvocation = (): { ok: true; value: unknown } | { ok: false } => {
    if (invocation().trim() === '') return { ok: true, value: null };
    try {
      return { ok: true, value: JSON.parse(invocation()) as unknown };
    } catch {
      return { ok: false };
    }
  };

  const invocationError = (): string | null => (parsedInvocation().ok ? null : invalidJson);

  return {
    name,
    setName,
    program,
    setProgram,
    backend,
    setBackend,
    baseUrl,
    setBaseUrl,
    model,
    setModel,
    kbRef,
    setKbRef,
    systemPrompt,
    setSystemPrompt,
    invocation,
    setInvocation,
    needsBaseUrl: () => agentBackendNeedsBaseUrl(backend() as AgentBackend),
    invocationError,
    valid: () => name().trim() !== '' && invocationError() === null,
    body: () => {
      const parsed = parsedInvocation();
      return {
        program: program(),
        backend: backend(),
        base_url: orNull(baseUrl()),
        model: orNull(model()),
        system_prompt: orNull(systemPrompt()),
        kb_ref: orNull(kbRef()),
        // Sent even when null: the API replaces every field, so an omitted
        // invocation is a reset. `valid()` gates the unparseable case, so this
        // is only reached with a real value.
        invocation: parsed.ok ? parsed.value : null,
      };
    },
    seed: (initial) =>
      batch(() => {
        // The defaults are the API's own column defaults (`program='custom'`,
        // `backend='none'`, every nullable column unset), so a create form shows
        // what a create would actually store.
        setName(initial?.name ?? '');
        setProgram(initial?.program ?? 'custom');
        setBackend(initial?.backend ?? 'none');
        setBaseUrl(initial?.base_url ?? '');
        setModel(initial?.model ?? '');
        setKbRef(initial?.kb_ref ?? '');
        setSystemPrompt(initial?.system_prompt ?? '');
        setInvocation(
          initial?.invocation == null ? '' : JSON.stringify(initial.invocation, null, 2),
        );
      }),
  };
}
